import { Button, Snackbar, Alert } from '@locus-taxy/encore-mui';
import { useEffect, useMemo, useState } from 'react';
import {
  SchemaModalBase,
  SchemaContent,
  ActionSelector,
} from '@locus-taxy/encore-grid';
import { useCopyFeedback } from '../hooks/useCopyFeedback';
import type { EntityMetadataResponse } from '../api/endpoints/useEntityMetadata';
import type { EntityDetailsAction } from '../types/actions';
import { getTestId } from '@locus-taxy/encore-utils';
import { TestIdAction } from '../utils/testIdUtils';

interface DetailsSchemaModalProps {
  open: boolean;
  onClose: () => void;
  entityMetadata: EntityMetadataResponse | undefined;
  entityName: string;
  actions: EntityDetailsAction[];
  entityData?: Record<string, unknown>;
}

export default function DetailsSchemaModal({
  open,
  onClose,
  entityMetadata,
  entityName,
  actions,
  entityData,
}: DetailsSchemaModalProps) {
  const { copyFeedback, handleCopy, closeCopyFeedback } = useCopyFeedback();
  const [activeTab, setActiveTab] = useState<
    'details' | 'actions' | 'data' | 'metadata'
  >('details');
  const [selectedAction, setSelectedAction] = useState<string>('');

  const detailsForm = entityMetadata?.data.frontend?.detailsView?.form?.schema;

  // Get available actions from the actions array
  const availableActions = useMemo(() => actions ?? [], [actions]);

  // Set first action as default if no action is selected and actions exist
  useEffect(() => {
    if (selectedAction === '' && availableActions.length > 0) {
      setSelectedAction(availableActions[0].id);
    }
  }, [selectedAction, availableActions]);

  if (!entityMetadata) return null;

  const tabButtons = (
    <>
      <Button
        variant={activeTab === 'details' ? 'contained' : 'outlined'}
        size="small"
        onClick={() => setActiveTab('details')}
        data-testid={getTestId({
          action: TestIdAction.TAB,
          object: 'details',
          entity: entityName,
          page: 'details',
        })}
      >
        Details View Form
      </Button>
      {availableActions.length > 0 && (
        <Button
          variant={activeTab === 'actions' ? 'contained' : 'outlined'}
          size="small"
          onClick={() => setActiveTab('actions')}
          data-testid={getTestId({
            action: TestIdAction.TAB,
            object: 'actions',
            entity: entityName,
            page: 'details',
          })}
        >
          Details View Actions
        </Button>
      )}
      {entityData && (
        <Button
          variant={activeTab === 'data' ? 'contained' : 'outlined'}
          size="small"
          onClick={() => setActiveTab('data')}
          data-testid={getTestId({
            action: TestIdAction.TAB,
            object: 'entity-data',
            entity: entityName,
            page: 'details',
          })}
        >
          Entity Data
        </Button>
      )}
      <Button
        variant={activeTab === 'metadata' ? 'contained' : 'outlined'}
        size="small"
        onClick={() => setActiveTab('metadata')}
        data-testid={getTestId({
          action: TestIdAction.TAB,
          object: 'metadata',
          entity: entityName,
          page: 'details',
        })}
      >
        Metadata
      </Button>
    </>
  );

  const renderContent = () => {
    switch (activeTab) {
      case 'details':
        return (
          <SchemaContent
            title="Details View Form"
            data={
              detailsForm as Record<string, unknown> | unknown[] | undefined
            }
            copyLabel="Details View Form"
            onCopy={handleCopy}
          />
        );
      case 'actions':
        return (
          <ActionSelector
            title="Details View Actions"
            actions={availableActions}
            selectedAction={selectedAction}
            onActionChange={setSelectedAction}
            onCopy={handleCopy}
          />
        );
      case 'data':
        return (
          <SchemaContent
            title="Entity Data"
            data={entityData}
            copyLabel="Entity Data"
            onCopy={handleCopy}
          />
        );
      case 'metadata':
        return (
          <SchemaContent
            title="Metadata"
            data={entityMetadata.data as Record<string, unknown>}
            copyLabel="Metadata"
            onCopy={handleCopy}
          />
        );
      default:
        return null;
    }
  };

  return (
    <>
      <SchemaModalBase
        open={open}
        onClose={onClose}
        entityName={entityName}
        tabButtons={tabButtons}
      >
        {renderContent()}
      </SchemaModalBase>

      <Snackbar
        open={copyFeedback.show}
        autoHideDuration={3000}
        onClose={closeCopyFeedback}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={closeCopyFeedback}
          severity={copyFeedback.severity}
          sx={{ width: '100%' }}
        >
          {copyFeedback.message}
        </Alert>
      </Snackbar>
    </>
  );
}
